import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase-client';
import { isRecoverableFirestoreAssertion } from './firestoreAssertionRecovery';
import { isFirestoreClientOfflineError } from './firestoreOffline';

const DEDUPE_WINDOW_MS = 5 * 1000;
const MAX_SHORT = 180;
const MAX_MEDIUM = 600;
const MAX_LONG = 3000;
const VALID_SEVERITIES = new Set(['info', 'warning', 'error', 'fatal']);

let initialized = false;
const recentReports = new Map();

function redact(value) {
  return String(value ?? '')
    .replace(/Bearer\s+[A-Za-z0-9._~+/-]+=*/gi, 'Bearer [redacted]')
    .replace(/AIza[0-9A-Za-z_-]{20,}/g, '[api-key]')
    .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '[email]')
    .replace(/\b[A-Za-z0-9_-]{48,}\b/g, '[token]');
}

function cleanLine(value, max = MAX_SHORT) {
  return redact(value)
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}

function cleanBlock(value, max = MAX_LONG) {
  return redact(value)
    .replace(/\r/g, '')
    .trim()
    .slice(0, max);
}

function cleanContextString(key, value) {
  if (key === 'loginAttemptAccountHash') {
    return String(value ?? '')
      .toLowerCase()
      .replace(/[^a-f0-9]/g, '')
      .slice(0, 64);
  }
  if (key === 'loginAttemptAccountLabel') {
    return String(value ?? '')
      .replace(/[\r\n\t@]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, MAX_MEDIUM);
  }
  return cleanLine(value, MAX_MEDIUM);
}

function normalizeSeverity(value) {
  const severity = cleanLine(value, 20).toLowerCase();
  return VALID_SEVERITIES.has(severity) ? severity : 'error';
}

function storageAvailable(type) {
  if (typeof window === 'undefined') return false;
  try {
    const storage = window[type];
    const key = '__memberNestStorageTest';
    storage.setItem(key, '1');
    storage.removeItem(key);
    return true;
  } catch (err) {
    return false;
  }
}

function indexedDbAvailable() {
  if (typeof window === 'undefined') return false;
  try {
    return Boolean(window.indexedDB);
  } catch (err) {
    return false;
  }
}

function cleanContext(context) {
  if (!context || typeof context !== 'object' || Array.isArray(context)) return {};
  return Object.entries(context).slice(0, 12).reduce((acc, [rawKey, rawValue]) => {
    const key = cleanLine(rawKey, 50);
    if (!key) return acc;
    if (typeof rawValue === 'string') {
      acc[key] = cleanContextString(key, rawValue);
    } else if (typeof rawValue === 'number' || typeof rawValue === 'boolean') {
      acc[key] = rawValue;
    }
    return acc;
  }, {});
}

function normalizeThrownValue(error) {
  if (error instanceof Error) {
    return {
      name: error.name || 'Error',
      code: error.code || '',
      message: error.message || '',
      stack: error.stack || '',
    };
  }

  if (error && typeof error === 'object') {
    return {
      name: cleanLine(error.name, 80) || 'Error',
      code: cleanLine(error.code, 80),
      message: cleanLine(error.message || error.reason || 'Non-error object thrown', MAX_MEDIUM),
      stack: cleanBlock(error.stack, MAX_LONG),
    };
  }

  return {
    name: 'Error',
    code: '',
    message: cleanLine(error || 'Unknown client error', MAX_MEDIUM),
    stack: '',
  };
}

function getClientDiagnostics() {
  if (typeof window === 'undefined') return {};
  const nav = window.navigator || {};
  return {
    host: window.location?.host || '',
    route: window.location ? `${window.location.pathname || ''}${window.location.search || ''}` : '',
    userAgent: nav.userAgent || '',
    browser: nav.userAgent || '',
    platform: nav.platform || '',
    vendor: nav.vendor || '',
    language: nav.language || '',
    online: nav.onLine !== false,
    cookieEnabled: nav.cookieEnabled === true,
    localStorageAvailable: storageAvailable('localStorage'),
    sessionStorageAvailable: storageAvailable('sessionStorage'),
    indexedDbAvailable: indexedDbAvailable(),
    viewport: {
      width: window.innerWidth || null,
      height: window.innerHeight || null,
    },
    clientCreatedAt: new Date().toISOString(),
  };
}

function getFingerprint(payload) {
  return [
    payload.source,
    payload.code,
    payload.name,
    payload.message,
    payload.route,
  ].join('|').slice(0, 700);
}

function shouldSend(payload) {
  const fingerprint = getFingerprint(payload);
  const now = Date.now();
  const previous = recentReports.get(fingerprint) || 0;
  recentReports.set(fingerprint, now);

  for (const [key, timestamp] of recentReports.entries()) {
    if (now - timestamp > DEDUPE_WINDOW_MS * 4) recentReports.delete(key);
  }

  return now - previous > DEDUPE_WINDOW_MS;
}

function isOpaqueScriptErrorEvent(event) {
  return !event?.error
    && event?.message === 'Script error.'
    && !event?.filename
    && Number(event?.lineno || 0) === 0
    && Number(event?.colno || 0) === 0;
}

function getThrownMessage(error) {
  if (error instanceof Error) return error.message || '';
  if (error && typeof error === 'object') return String(error.message || error.reason || '');
  return String(error || '');
}

export function isExternalRuntimeUpdateRejection(error) {
  return /^Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+$/.test(getThrownMessage(error));
}

export function reportCaughtError(error, options = {}) {
  const normalized = normalizeThrownValue(error);
  const payload = {
    ...getClientDiagnostics(),
    source: cleanLine(options.source, 80) || 'caught',
    severity: normalizeSeverity(options.severity),
    name: cleanLine(normalized.name, 80),
    code: cleanLine(normalized.code, 80),
    message: cleanLine(normalized.message, MAX_MEDIUM),
    stack: cleanBlock(normalized.stack, MAX_LONG),
    componentStack: cleanBlock(options.componentStack, MAX_LONG),
    context: cleanContext(options.context),
    authUidHint: auth?.currentUser?.uid || '',
  };

  if (!payload.message || (!options.bypassDedupe && !shouldSend(payload))) return Promise.resolve();

  try {
    const reportClientError = httpsCallable(functions, 'reportClientError');
    return Promise.resolve(reportClientError(payload)).catch((loggingError) => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Client error reporting failed', loggingError);
      }
    });
  } catch (loggingError) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Client error reporting failed', loggingError);
    }
    return Promise.resolve();
  }
}

export function initializeClientErrorReporting() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  window.addEventListener('error', (event) => {
    if (isOpaqueScriptErrorEvent(event)) return;
    if (isRecoverableFirestoreAssertion(event.error || event.message)) return;
    if (isFirestoreClientOfflineError(event.error || event.message)) return;

    reportCaughtError(event.error || event.message, {
      source: 'window.error',
      severity: 'error',
      context: {
        filename: event.filename || '',
        lineno: event.lineno || 0,
        colno: event.colno || 0,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isRecoverableFirestoreAssertion(event.reason)) return;
    if (isFirestoreClientOfflineError(event.reason)) return;
    if (isExternalRuntimeUpdateRejection(event.reason)) return;

    reportCaughtError(event.reason, {
      source: 'window.unhandledrejection',
      severity: 'error',
    });
  });
}
